/**
 * What the preview-first personalization flow SAYS about itself (ui#1814, telemetry criterion).
 *
 * Plain TypeScript — no React import, and no transport. The split is the owner's 2026-08-27 rule: the
 * business logic emits domain events and telemetry SUBSCRIBES to them, so measuring the funnel never
 * becomes a reason to reach into the flow. `personalizationEventBeacon` is the only subscriber that
 * sends anything, and the builder knows nothing about it.
 *
 * The builder is `opened` and `completed`: how many senders composed anything, and how many finished. Each
 * carries the moment it happened at, because a builder raised over the upload and one opened from the success
 * screen are different behaviours that a single rate cannot tell apart. The offer's `shown` and `declined`
 * went with the Basic strip they counted (kitecorp/transferify#525): every plan includes the look, so there is
 * no offer to show or decline.
 */

/**
 * The counters this flow publishes.
 *
 * Dotted metric names rather than an enum of bare words, because that is what they are: the api
 * aggregates them under one `personalization.` family, beside the `over-limit` one.
 */
export type PersonalizationCounter =
	| "personalization.builder.opened"
	| "personalization.builder.completed";

/**
 * WHERE the sender met the builder. Upper-case because the api spells its closed vocabularies as enum
 * NAMES on the wire, the same form `ConsentSurface` uses.
 *
 * - `UPLOAD` — the builder that opens itself in the upload's dead time (prototype state A).
 * - `SEND`: the builder opened from the send panel's Personalise button.
 * - `LATE`: the builder opened from the success screen, after the send.
 */
export type PersonalizationMoment = "UPLOAD" | "SEND" | "LATE";

/** One thing that happened, and where. */
export interface PersonalizationEvent {
	counter: PersonalizationCounter;
	moment: PersonalizationMoment;
}

export type PersonalizationEventListener = (event: PersonalizationEvent) => void;

export type Unsubscribe = () => void;

/**
 * The seam the create flow holds: two things it can say, and a way for others to listen.
 *
 * Nothing is sold here (kitecorp/transferify#525): every plan includes the look, so there is no purchase step
 * and no offer to count.
 */
export interface PersonalizationEventEmitter {
	/** The builder came on screen at `moment`. */
	builderOpened(moment: PersonalizationMoment): void;
	/** The sender left the builder KEEPING what they composed — the commit, not the dismissal. */
	builderCompleted(moment: PersonalizationMoment): void;
	subscribe(listener: PersonalizationEventListener): Unsubscribe;
}

/**
 * A module singleton, unlike the per-page download emitter.
 *
 * The subject here spans surfaces that are never mounted together — the create panel, the builder over
 * the progress card, the success screen — and one sender's funnel runs across all of them. A
 * per-component emitter would end at each unmount and split one journey into three, which is precisely
 * the number this measurement exists to produce.
 */
export function createPersonalizationEventEmitter(): PersonalizationEventEmitter {
	const listeners = new Set<PersonalizationEventListener>();

	function emit(counter: PersonalizationCounter, moment: PersonalizationMoment): void {
		// Iterated over a copy so a listener may unsubscribe from inside its own callback without
		// skipping the listener that follows it.
		[...listeners].forEach(listener => listener({counter, moment}));
	}

	return {
		builderOpened: moment => emit("personalization.builder.opened", moment),
		builderCompleted: moment => emit("personalization.builder.completed", moment),
		subscribe(listener) {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
			};
		},
	};
}

/** The app's emitter. One per document, for the reason {@link createPersonalizationEventEmitter} gives. */
export const personalizationEvents: PersonalizationEventEmitter = createPersonalizationEventEmitter();
